import { assert } from '~/lib/customAssert'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import { Box, Button, Grid } from '@mui/material'
import { useState } from 'react'
import ImageUploading, { ImageListType } from 'react-images-uploading'
import { ALLOWED_IMAGE_EXTENSIONS, fileToDataURL, isValidImageFile } from '~/lib/imageProcessing'
import { ImageCropDialog } from './ImageCropDialog'
import { Reorderable } from './Reorderable'

const useCropDialog = () => {
  const [dialogState, setDialogState] = useState<{
    image: Blob
    onSave: (image: Blob) => void
    onClose: () => void
  } | null>(null)

  return {
    requestCropImage: (image: Blob): Promise<Blob | null> => {
      return new Promise((resolve) => {
        setDialogState({
          image,
          onSave: (image) => {
            resolve(image)
            setDialogState(null)
          },
          onClose: () => {
            resolve(null)
            setDialogState(null)
          },
        })
      })
    },

    cropDialogContent: dialogState && (
      <ImageCropDialog image={dialogState.image} onSave={dialogState.onSave} onCancel={dialogState.onClose} />
    ),
  }
}

export const ImagePicker = (props: {
  value: ImageListType
  onChange: (value: ImageListType) => void
  multiple?: boolean
  maxNumber?: number
  minNumber?: number
  disabled?: boolean
}) => {
  const { cropDialogContent, requestCropImage } = useCropDialog()

  const onImageSelectedOrDeleted = async (imageList: ImageListType, addUpdatedIndex?: number[]) => {
    // Note: 画像削除時はaddUpdatedIndexがundefined
    if (!addUpdatedIndex) {
      props.onChange(imageList)
    } else {
      const newSelectedImages = imageList
        .map((image, index) => {
          if (!image.file) {
            return null
          }
          return { file: image.file, index }
        })
        .filter((img): img is { file: File; index: number } => img !== null && addUpdatedIndex.includes(img.index))

      // 不正な拡張子でないか検査する
      const validationResults = await Promise.all(newSelectedImages.map((image) => isValidImageFile(image.file)))
      if (validationResults.includes(false)) {
        // なんかダイアログの方が良いかも
        window.alert('画像ファイルの内容と拡張子が一致しない画像が選択されました。画像ファイルをご確認ください。')
        return
      }

      const newImageList = [...imageList]
      for (const image of newSelectedImages) {
        const croppedImage = await requestCropImage(image.file)
        if (croppedImage) {
          // ファイル名が240文字以上だと、サーバー側でエラーになるため
          const fileName = image.file.name.slice(0, 200)
          const file = new File([croppedImage], fileName, {
            type: croppedImage.type,
          })

          newImageList[image.index] = {
            file,
            dataURL: await fileToDataURL(file),
          }
        } else {
          // キャンセル時は選択した画像を削除する
          newImageList.splice(image.index, 1)
        }
      }
      props.onChange(newImageList)
    }
  }

  return (
    <>
      <ImageUploading
        value={props.value}
        onChange={onImageSelectedOrDeleted}
        multiple={props.multiple}
        maxNumber={props.maxNumber}
        acceptType={ALLOWED_IMAGE_EXTENSIONS.slice()} // readonly Arrayのままだと渡せないのでslice()でコピーしている
      >
        {({ imageList, onImageUpload, onImageUpdate, onImageRemove, dragProps }) => (
          <Box>
            <Box mb={2} p={1} {...dragProps}>
              <Button
                variant="contained"
                startIcon={<PhotoCamera />}
                disabled={imageList.length >= (props.maxNumber ?? 1) || props.disabled}
                onClick={onImageUpload}
              >
                画像を追加する
              </Button>
            </Box>

            <Grid container direction="row" spacing={5}>
              <Reorderable
                items={imageList}
                onReorder={(reorderdItems) => {
                  props.onChange(reorderdItems)
                }}
                disabled={props.disabled}
                renderItem={({ item: image, dragProps, isDragging }, index) => {
                  assert(image.dataURL) // 基本ないはず
                  return (
                    <Grid key={index} {...dragProps} style={{ opacity: isDragging ? 0.5 : 1, cursor: 'move' }}>
                      <Box width={200} height={200} borderRadius="4px" overflow="hidden">
                        <img
                          src={image.dataURL}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          alt=""
                        />
                      </Box>
                      <Box mt={2.5}>
                        <Grid container direction="row" justifyContent="space-evenly">
                          <Button variant="contained" onClick={() => onImageUpdate(index)} disabled={props.disabled}>
                            変更
                          </Button>
                          <Button
                            color="inherit"
                            variant="outlined"
                            onClick={() => onImageRemove(index)}
                            disabled={props.disabled || (props.minNumber ? props.minNumber > index : false)}
                          >
                            削除
                          </Button>
                        </Grid>
                      </Box>
                    </Grid>
                  )
                }}
              />
            </Grid>
          </Box>
        )}
      </ImageUploading>
      {cropDialogContent}
    </>
  )
}
